import { formatSignalValue } from './SignalFormatting';
import type { SignalData, SignalDatum, SignalDelta, SignalRng, SignalSeverity, SignalValueFormat } from './SignalTypes';

/** FNV-1a string hash → 32-bit seed. */
function hashSeed(str: string): number {
    let h = 2166136261 >>> 0;
    for (let i = 0; i < str.length; i++) {
        h ^= str.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

/** mulberry32 PRNG — tiny, fast, and fully deterministic from a numeric seed. */
function mulberry32(seed: number): () => number {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/** Builds a deterministic RNG keyed by a string seed (typically a tile id). */
export function makeRng(seed: string): SignalRng {
    const next = mulberry32(hashSeed(seed));
    return {
        next,
        int: (min, max) => Math.floor(next() * (max - min + 1)) + min,
        float: (min, max) => min + next() * (max - min),
        pick: (items): (typeof items)[number] => {
            if (items.length === 0) throw new Error('SignalRng.pick called with an empty list');
            return items[Math.floor(next() * items.length)] as (typeof items)[number];
        },
    };
}

/** A gently trending series of `n` values in roughly [min,max], biased by `trend`. */
export function sparkSeries(rng: SignalRng, n: number, min: number, max: number, trend: 'up' | 'down' | 'flat' = 'flat'): number[] {
    const out: number[] = [];
    const drift = trend === 'up' ? (max - min) / n : trend === 'down' ? -(max - min) / n : 0;
    let level = trend === 'up' ? min : trend === 'down' ? max : (min + max) / 2;
    for (let i = 0; i < n; i++) {
        const jitter = rng.float(-(max - min) * 0.12, (max - min) * 0.12);
        level = Math.max(min, Math.min(max, level + drift + jitter));
        out.push(Math.round(level));
    }
    return out;
}

function makeDelta(rng: SignalRng, range: [number, number], goodWhen: 'up' | 'down' | 'neutral'): SignalDelta {
    const pct = rng.float(range[0], range[1]);
    const direction = Math.abs(pct) < 0.5 ? 'flat' : pct > 0 ? 'up' : 'down';
    const tone = goodWhen === 'neutral' || direction === 'flat' ? 'neutral' : direction === goodWhen ? 'good' : 'bad';
    return { label: `${pct >= 0 ? '+' : ''}${pct.toFixed(0)}%`, direction, tone };
}

export interface KpiSampleOptions {
    min: number;
    max: number;
    format?: SignalValueFormat;
    caption?: string;
    /** When set, attach a delta drawn from this percentage range. */
    deltaPct?: [number, number];
    /** Which direction reads as good for the delta tone. Defaults to `up`. */
    goodWhen?: 'up' | 'down' | 'neutral';
    /** When set, attach a 12-point sparkline trending this way. */
    spark?: 'up' | 'down' | 'flat';
}

/** A single headline number, optionally with a delta chip and sparkline. */
export function kpiSample(rng: SignalRng, o: KpiSampleOptions): SignalData {
    const value = o.format === 'percent' || o.format === 'ratio' ? rng.float(o.min, o.max) : rng.int(o.min, o.max);
    const data: SignalData = { headline: formatSignalValue(o.format, value), format: o.format, caption: o.caption };
    if (o.deltaPct) data.delta = makeDelta(rng, o.deltaPct, o.goodWhen ?? 'up');
    if (o.spark) data.spark = sparkSeries(rng, 12, Math.max(0, o.min * 0.6), o.max, o.spark).map((v) => Math.round(v));
    return data;
}

export interface SeriesSampleOptions {
    min: number;
    max: number;
    format?: SignalValueFormat;
    /** Sort descending by value (default true) so bars read as a ranking. */
    sort?: boolean;
}

/** A labelled value series for `bars` or `donut` bodies. */
export function seriesSample(rng: SignalRng, labels: readonly string[], o: SeriesSampleOptions): SignalData {
    const series: SignalDatum[] = labels.map((label) => ({
        label,
        value: o.format === 'percent' ? rng.float(o.min, o.max) : rng.int(o.min, o.max),
    }));
    if (o.sort !== false) series.sort((a, b) => b.value - a.value);
    return { series, format: o.format };
}

/** A bucketed time series for a `trend` body; `labels` are the x-axis ticks. */
export function trendSample(rng: SignalRng, labels: readonly string[], min: number, max: number, trend: 'up' | 'down' | 'flat' = 'up'): SignalData {
    const values = sparkSeries(rng, labels.length, min, max, trend);
    return { series: labels.map((label, i) => ({ label, value: values[i] ?? 0 })), format: 'compact' };
}

/** A heatmap grid of `rows` × `cols` with random intensities. */
export function heatSample(rng: SignalRng, rowLabels: readonly string[], colLabels: readonly string[], min: number, max: number, format?: SignalValueFormat): SignalData {
    return {
        heat: {
            columns: [...colLabels],
            format,
            rows: rowLabels.map((label) => ({ label, values: colLabels.map(() => rng.int(min, max)) })),
        },
    };
}

export interface GaugeSampleOptions {
    min: number;
    max: number;
    caption?: string;
    /** `percent` renders the value as a %, otherwise a compact count over an implicit scale. */
    format?: SignalValueFormat;
}

/** A radial gauge whose fill is the value's position in [min,max]. */
export function gaugeSample(rng: SignalRng, o: GaugeSampleOptions): SignalData {
    const fraction = rng.float(o.min, o.max);
    const valueText = o.format === 'ratio' ? `${fraction.toFixed(2)}` : formatSignalValue(o.format ?? 'percent', fraction);
    return { gauge: { fraction: o.format === 'percent' || o.format === undefined ? fraction : Math.min(1, fraction), valueText, caption: o.caption } };
}

export interface AlertTemplate {
    label: string;
    detail?: string;
    severity: SignalSeverity;
}

/** An alert-list body: a headline open-count plus a few representative rows. */
export function alertsSample(rng: SignalRng, templates: readonly AlertTemplate[], countRange: [number, number]): SignalData {
    return {
        alertCount: rng.int(countRange[0], countRange[1]),
        alerts: templates.map((t) => ({ ...t, meta: relativeTime(rng) })),
    };
}

const TIME_UNITS = ['m', 'h'] as const;
function relativeTime(rng: SignalRng): string {
    const unit = rng.pick(TIME_UNITS);
    return unit === 'm' ? `${rng.int(2, 59)}m ago` : `${rng.int(1, 23)}h ago`;
}
